import { brandingApi } from '@/features/settings/branding/api'
import { ApiError, api, isUnauthorized, withEnvironments, type ApiErrorBody, type AuthSession, type MyPermissions, type Website } from '@/lib/api'
import { ENVIRONMENT_LABELS } from '@/lib/environment'
import { resolvePeriod, toDateKey, type GlobalFilters } from '@/lib/filters'

import {
  activationStatus,
  activityFromRecords,
  CampaignPartiallySavedError,
  audienceToRefs,
  automationRef,
  campaignFromWire,
  channelFromWire,
  CREATIVE_SLOTS,
  creativeFromWire,
  emptyMetrics,
  isOwnedChannel,
  OBJECTIVE_TO_API,
  performanceFromWire,
  toWireDate,
  trackingFromWire,
  type CampaignCreative,
  type CampaignDetail,
  type CampaignDraft,
  type CampaignListItem,
  type CampaignOut,
  type CampaignPatch,
  type CampaignPerformance,
  type CampaignPerformanceOut,
  type CampaignWebsite,
  type ChannelOut,
  type ChannelPatch,
  type CreativeOut,
  type CreativePatch,
  type ListOut,
  type SaveIntent,
  type TrackingGenerateInput,
  type TrackingOut,
  type TrackingPatch,
} from './campaigns'
import type { ActionDefinition, ActivityEntry, AutomationStatus, TriggerDefinition } from './automation'
import {
  advertisingReportFromWire,
  advertisingSearch,
  type AdvertisingCampaignReportOut,
} from './advertising'
import {
  adAttributionReconciliationFromWire,
  adReconciliationSearch,
  advertisingOverviewFromWire,
  advertisingOverviewSearch,
  advertisingOverviewTrendFromWire,
  advertisingOverviewTrendSearch,
  type AdAttributionReconciliation,
  type AdAttributionReconciliationOut,
  type AdOverviewQuery,
  type AdOverviewTrendQuery,
  type AdReconciliationQuery,
  type AdvertisingOverview,
  type AdvertisingOverviewOut,
  type AdvertisingOverviewTrend,
  type AdvertisingOverviewTrendOut,
} from './advertisingOverview'
import {
  attributionFromWire,
  attributionSearch,
  type AttributionConversionsOut,
  type AttributionQuery,
  type AttributionReport,
} from './attribution'
import {
  recoveryListFromWire,
  recoveryPath,
  recoverySummaryFromWire,
  type RecoverableCheckoutsList,
  type RecoverableCheckoutsListOut,
  type RecoverableCheckoutsSummary,
  type RecoverableCheckoutsSummaryOut,
  type RecoveryQuery,
} from './recovery'
import {
  competitionsFromWire,
  competitionsSearch,
  type CompetitionsList,
  type CompetitionsQuery,
} from './competitions'
import {
  competitionAdvertisingFromWire,
  competitionDetailFromWire,
  competitionDetailPath,
  competitionDetailSearch,
  competitionDevicesFromWire,
  competitionFunnelFromWire,
  competitionSourcesFromWire,
  type CompetitionAdvertising,
  type CompetitionAdvertisingOut,
  type CompetitionDetail,
  type CompetitionDetailOut,
  type CompetitionDetailQuery,
  type CompetitionDevices,
  type CompetitionDevicesOut,
  type CompetitionFunnel,
  type CompetitionFunnelOut,
  type CompetitionSources,
  type CompetitionSourcesOut,
  type SalesGranularity,
} from './competitionDetail'
import {
  customerRevealFromWire,
  customerSearchBody,
  customersFromWire,
  customersSearch,
  type CustomerReveal,
  type CustomerRevealOut,
  type CustomerSearch,
  type CustomersList,
  type CustomersQuery,
} from './customers'
import {
  activeAlertsPath,
  alertActionPath,
  digestPath,
  digestReportFromWire,
  insightAlertFromWire,
  regenerateDigestPath,
  trendFromWire,
  trendsMeta,
  TRENDS_PATH,
  type DigestReportOut,
  type InsightAlertOut,
  type InsightAlertsOut,
  type TrendsOut,
} from './insights'
import { csvFileFromResponse, type CsvFile } from './list'
import {
  leakageFromWire,
  leakageSearch,
  lifecycleFromWire,
  lifecycleSearch,
  ordersFromWire,
  ordersSearch,
  REVENUE_LEAKAGE_PATHS,
  type LeakageQuery,
  type LifecycleQuery,
  type OrdersQuery,
  type RevenueLeakage,
  type RevenueLeakageOut,
  type RevenueLifecycle,
  type RevenueLifecycleOut,
  type RevenueOrders,
  type RevenueOrdersOut,
} from './revenueLeakage'
import { signupsFromWire, type SignupsReport } from './signups'
import {
  firstPartyTrafficFromWire,
  firstPartyTrafficSearch,
  type FirstPartyTrafficOut,
  type FirstPartyTrafficQuery,
  type FirstPartyTrafficReport,
} from './firstPartyTraffic'
import {
  DATA_QUALITY_BASELINE_PATH,
  trackingBaselineFromWire,
  trackingBaselineToWire,
  type TrackingBaseline,
  type TrackingBaselineInput,
  type TrackingBaselineOut,
} from './dataQuality'
import {
  REVENUE_PATHS,
  revenueBreakdownFromWire,
  revenueBreakdownSearch,
  revenueSearch,
  revenueSeriesFromWire,
  revenueSeriesSearch,
  revenueSummaryFromWire,
  revenueTimingFromWire,
  type RevenueBreakdown,
  type RevenueBreakdownOut,
  type RevenueBreakdownQuery,
  type RevenueMetaOut,
  type RevenueQuery,
  type RevenueSeries,
  type RevenueSeriesQuery,
  type RevenueSummary,
  type RevenueSummaryOut,
  type RevenueTiming,
  type RevenueTimingOut,
  type RevenueSeriesOut,
} from './revenue'
import type { ApiClient } from './client'
import { NotLiveApiClient } from './NotLiveApiClient'
import {
  channelPerformanceFromWire,
  channelPerformanceSearch,
  entitlementsFromFlags,
  FRESHNESS_PATH,
  freshnessFromWire,
  funnelFromWire,
  kpisFromWire,
  overviewMeta,
  overviewSearch,
  revenueTrendFromWire,
  revenueTrendSearch,
  topCompetitionsFromWire,
  topCompetitionsSearch,
  trendFilters,
  type ChannelPerformanceOut,
  type FeatureFlagsOut,
  type FunnelOut,
  type KpisOut,
  type OverviewMetaOut,
  type RevenueTrendOut,
  type TopCompetitionsOut,
} from './overview'
import {
  ga4ConnectErrorFrom,
  ga4ImportRunFromWire,
  ga4IntegrationFromWire,
  ga4PropertyFromWire,
  ga4StreamCheckFromWire,
  trafficFromWire,
  trafficSearch,
  type Ga4ImportRunOut,
  type Ga4IntegrationOut,
  type Ga4PropertyOut,
  type Ga4StreamCheckOut,
  type TrafficOut,
} from './ga4'
import {
  adAccountsFromWire,
  mappingFromWire,
  mappingToWire,
  metaStateFromWire,
  type AdAccountListOut,
  type MetaConnectStart,
  type AdAccountMappingOut,
  type MetaIntegrationOut,
} from './integrations'
import type { CompetitorInput, TrackedCompetitionInput } from './market'
import type { ReportDataset, ReportExport, ReportInput, ReportRow, ReportSchedule, RequestExportInput } from './reports'
import type {
  FieldGroupKey,
  ListingInput,
  ListingTemplateInput,
  PublishTargetKey,
} from './listings'
import {
  isPermission,
  ROLE_LABELS,
  type ChannelPerformance,
  type AttributionModel,
  type CompetitionPerformance,
  type DataMeta,
  type Entitlements,
  type FunnelStage,
  type Kpi,
  type RevenueTrend,
  type DigestPeriod,
  type DigestQuery,
  type DigestReport,
  type Trend,
  type FilterOptions,
  type Envelope,
  type Environment,
  type HealthStatus,
  type SessionUser,
  type TenantSummary,
  type TrendWindow,
  type AdvertisingQuery,
  type AdvertisingReport,
  type Ga4Integration,
  type Ga4ImportRun,
  type Ga4Property,
  type Ga4StreamCheck,
  type Ga4TrafficQuery,
  type Ga4TrafficReport,
  type MetaAdAccountList,
  type MetaAdAccountMapping,
  type MetaIntegrationState,
  type MetaMappingResult,
  type Alert,
  type WebsiteHealth,
  type WebsiteHealthStatus,
} from './types'

/** Whether a caller may read analytics: the Overview routes (and so the shell's freshness) need it. */
function readsAnalytics(me: MyPermissions): boolean {
  return me.permissions.includes('analytics.read')
}

/** The base every `/api/v1` call resolves against: JSON goes through `send`, downloads are linked with it. */
export const API_BASE = '/api/v1'

/** The `ApiError` for a non-2xx response: the API's own code and message when it sent them. */
async function apiErrorFrom(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | undefined
  try {
    body = (await response.json()) as ApiErrorBody
  } catch {
    body = undefined
  }
  return new ApiError(
    response.status,
    body?.error?.code ?? 'temporary_failure',
    body?.error?.message ?? response.statusText,
    body?.correlation_id,
    body?.error?.details ?? {},
  )
}

/**
 * The delegation list: every `ApiClient` method with no real endpoint yet, in
 * one place. `HttpApiClient` forwards exactly these to its not-live delegate
 * and may call nothing else on it (the delegate's type is `NotLiveApi`), so a
 * section is not live precisely while its methods are listed here. When a
 * read model lands, its methods leave this list for a real call below, and
 * the compiler points at each screen gate still naming them
 * (docs/architecture/web-not-live-sections.md).
 */
export const NOT_LIVE_READS = [
  'recommendations',
  'automations',
  'triggerDefinitions',
  'actionDefinitions',
  'workflows',
  'workflow',
  'scheduledActions',
  'automationActivity',
  'savedReports',
  'savedReport',
  'reportRows',
  'exports',
  'marketCompetitors',
  'marketTrackedCompetitions',
  'marketObservations',
  'marketActivitySummary',
  'marketPricingSummary',
  'marketPrizeTrends',
  'marketMonitoringStatus',
  'listings',
  'listing',
  'listingTemplates',
] as const satisfies readonly (keyof ApiClient)[]

/** The delegated methods that change something: the not-live client refuses every one. */
export const NOT_LIVE_MUTATIONS = [
  'dismissRecommendation',
  'setWorkflowStatus',
  'cancelScheduledAction',
  'createSavedReport',
  'duplicateSavedReport',
  'scheduleSavedReport',
  'requestExport',
  'addMarketCompetitor',
  'importMarketCompetitorsCsv',
  'addTrackedCompetition',
  'importTrackedCompetitionsCsv',
  'createListing',
  'updateListing',
  'generateListingContent',
  'setListingApproval',
  'publishListing',
  'saveListingTemplate',
] as const satisfies readonly (keyof ApiClient)[]

export type NotLiveMethod = (typeof NOT_LIVE_READS)[number] | (typeof NOT_LIVE_MUTATIONS)[number]

/** What the delegate answers: the listed methods and nothing else. */
export type NotLiveApi = Pick<ApiClient, NotLiveMethod>

/**
 * The list as a set. `answersNotLive` still checks membership, although its
 * parameter's type already says the same, because a caller can widen a string
 * into `NotLiveMethod` with a cast; that must answer false, not "not live".
 */
const NOT_LIVE_METHODS: ReadonlySet<NotLiveMethod> = new Set<NotLiveMethod>([...NOT_LIVE_READS, ...NOT_LIVE_MUTATIONS])

/**
 * Whether `client` answers `method` as not live — known before any request
 * resolves, so a screen never flashes a control it is about to withdraw. True
 * only for the production client on its default not-live delegate; the demo
 * client, and an HTTP client handed a demo delegate, answer false. `method` is
 * typed to the delegation list, so a screen still probing a method that has
 * gone live stops compiling.
 */
export function isNotLiveMethod(client: ApiClient, method: NotLiveMethod): boolean {
  return client instanceof HttpApiClient && client.answersNotLive(method)
}

export interface HttpApiClientOptions {
  /**
   * Answers the delegation list. Defaults to `NotLiveApiClient`: empty
   * datasets badged "Not live yet" and refused mutations, so a real tenant
   * never sees generated figures. Passing a `MockApiClient` here is opt-in
   * (tests only); the demo build uses `MockApiClient` directly.
   */
  delegate?: NotLiveApi
}

/**
 * `GET /auth/session` while TmT support is inside the tenant: `SessionOut`'s
 * `impersonation` field (blueprint 03 §3.9). Absent or `null` on every other
 * session, so an older API reads as "not an impersonation".
 */
type AuthSessionWithImpersonation = AuthSession & {
  impersonation?: { actor: string; reason: string; expires_at: string; elevated: boolean } | null
}

/**
 * The real API, for the endpoints that exist on `main`: health, branding
 * settings, the caller's own permissions (`/rbac/me`), the environment
 * selector, daily metrics, the tenant's websites, feature flags and campaigns
 * (with their channels, creatives, tracking, QR codes and performance), the
 * Overview's read models (`./overview`), and Insights' alerts, trends and
 * digest (`./insights`). Every method on
 * the delegation list above goes to the not-live delegate — never to an
 * endpoint that does not exist — and says so in its `meta.source`.
 */
export class HttpApiClient implements ApiClient {
  readonly kind = 'http' as const
  private readonly notLive: NotLiveApi
  private logo: { url: string } | null = null

  constructor(options: HttpApiClientOptions = {}) {
    this.notLive = options.delegate ?? new NotLiveApiClient()
  }

  /** See `isNotLiveMethod`. */
  answersNotLive(method: NotLiveMethod): boolean {
    return this.notLive instanceof NotLiveApiClient && NOT_LIVE_METHODS.has(method)
  }

  async health(): Promise<HealthStatus> {
    const response = await fetch('/api/v1/health', { credentials: 'same-origin' })
    if (!response.ok) throw new ApiError(response.status, 'temporary_failure', response.statusText)
    return (await response.json()) as HealthStatus
  }

  async currentTenant(): Promise<TenantSummary> {
    const branding = await brandingApi.current()
    return {
      id: 'current',
      name: branding.display_name ?? 'Your organisation',
      logoUrl: branding.has_logo ? await this.logoUrl() : this.clearLogo(),
      // Branding settings carry no accent yet; the default theme applies.
      accentColor: null,
      currency: branding.currency,
      locale: branding.locale,
      timezone: branding.timezone,
      current: true,
    }
  }

  /** One object URL for the logo, revoked when replaced so refetches do not leak. */
  private async logoUrl(): Promise<string | null> {
    const next = await brandingApi.logoObjectUrl()
    this.clearLogo()
    this.logo = next ? { url: next } : null
    return next
  }

  private clearLogo(): null {
    if (this.logo) URL.revokeObjectURL(this.logo.url)
    this.logo = null
    return null
  }

  async tenants(): Promise<TenantSummary[]> {
    // A session belongs to one tenant; others are reached on their own host.
    return [await this.currentTenant()]
  }

  async session(): Promise<SessionUser> {
    const [permissions, identity] = await Promise.allSettled([this.myPermissions(), api.authSession()])

    // A 401 from *either* call is the definitive answer — this browser holds no
    // session — and must win over any other failure, which is only a failure to
    // load. Settling both rather than racing them is what makes that
    // deterministic: with `Promise.all`, a 503 from one call could arrive first
    // and hide a real expiry behind a generic error.
    for (const outcome of [permissions, identity]) {
      if (outcome.status === 'rejected' && isUnauthorized(outcome.reason)) throw outcome.reason
    }
    if (permissions.status === 'rejected') throw permissions.reason

    // Anything else wrong with the identity lookup costs the user menu a display
    // name, which is not worth failing the whole shell over.
    const me = permissions.value
    const who: AuthSessionWithImpersonation | null = identity.status === 'fulfilled' ? identity.value : null
    const role = me.role ?? who?.role.value ?? null
    // `developer_support` is never a member's role: it is only ever a TmT support
    // impersonation (ADR-0003 D3.9). Without the identity lookup the banner has
    // nothing to say, and an impersonated shell with no banner is the hidden mode
    // the banner exists to rule out — so this one failure does fail the shell.
    if (!who && role === 'developer_support' && identity.status === 'rejected') throw identity.reason
    const impersonation = who?.impersonation ?? null
    return {
      userId: who?.user_id ?? null,
      name: who?.display_name ?? null,
      email: who?.email ?? null,
      role,
      roleLabel: who?.role.label ?? (role ? (ROLE_LABELS[role] ?? role) : 'No role'),
      permissions: me.permissions.filter(isPermission),
      impersonation: impersonation
        ? {
            actor: impersonation.actor,
            reason: impersonation.reason,
            expiresAt: impersonation.expires_at,
            elevated: impersonation.elevated,
          }
        : null,
    }
  }

  async signOut(): Promise<string | null> {
    const { provider_logout_url } = await api.logout()
    return provider_logout_url
  }

  environmentSelector(environments: readonly Environment[]) {
    return api.environmentSelector(environments)
  }

  dailyMetrics(environments: readonly Environment[]) {
    return api.dailyMetrics(environments)
  }

  /**
   * The filter vocabulary from what is real today: the tenant's websites, in
   * every environment and grouped by it, so a staging site is never mistaken
   * for production. Every other dimension (competitions, channels,
   * campaigns, creatives, devices, customer types) has no endpoint yet, so it
   * offers nothing rather than another tenant's demo names.
   */
  async filterOptions(): Promise<FilterOptions> {
    const websites = await api.websites(ALL_ENVIRONMENTS)
    return {
      website: websites.map((website) => ({ value: website.id, label: website.name, group: ENVIRONMENT_LABELS[website.environment] })),
      competition: [],
      channel: [],
      campaign: [],
      creative: [],
      device: [],
      customer_type: [],
    }
  }

  /** The plan last read in this session, so a failed re-read keeps the pages it already allowed. */
  private plan: Entitlements | null = null

  /**
   * The plan's modules from the tenant's resolved feature flags
   * (`entitlementsFromFlags`). A re-read that fails (other than a lapsed
   * session, which the signed-out gate must see) answers the plan already read
   * rather than withdrawing every page over a passing outage; with no plan read
   * yet, the failure stands and the route gate says access could not be confirmed.
   */
  async entitlements(): Promise<Entitlements> {
    try {
      this.plan = entitlementsFromFlags(await this.send<FeatureFlagsOut>('/tenancy/feature-flags'))
      return this.plan
    } catch (error) {
      if (this.plan && !isUnauthorized(error)) return this.plan
      throw error
    }
  }

  /**
   * How current the website's commerce data is: the Overview trend's freshness
   * (`FRESHNESS_PATH` says why that route). The top bar polls this on every
   * page, so a role without `analytics.read` (TmT support's
   * `developer_support`) is refused here without a request: the route would
   * refuse it too, and the API audits every refusal.
   */
  async freshness(): Promise<DataMeta> {
    if (!(await this.canReadAnalytics())) {
      throw new ApiError(403, 'authorization_error', 'this action requires analytics.read')
    }
    return freshnessFromWire(await this.send<RevenueTrendOut>(FRESHNESS_PATH))
  }

  /** Whether this session holds `analytics.read`, as `myPermissions` last answered; null when it has not, or failed. */
  private analyticsReadable: Promise<boolean> | null = null

  /**
   * `GET /rbac/me` for this browser's session cookie: the one place this client
   * reads the caller's permissions (`session()` and the freshness gate alike),
   * and the one place that keeps `analyticsReadable` in step with it — set by
   * every answer, cleared by every failure (a lapsed session, an impersonation
   * ending), so the gate never runs on a stale answer.
   */
  private async myPermissions(): Promise<MyPermissions> {
    try {
      const me = await api.myPermissions()
      this.analyticsReadable = Promise.resolve(readsAnalytics(me))
      return me
    } catch (error) {
      this.analyticsReadable = null
      throw error
    }
  }

  /** The last answer when there is one, else asked now (a failure clears it, so the next poll asks again). */
  private canReadAnalytics(): Promise<boolean> {
    this.analyticsReadable ??= this.myPermissions().then(readsAnalytics)
    return this.analyticsReadable
  }

  // --- overview: the wave 10 read models (api/overview.ts) ----------------------

  /** The live envelope's meta for an Overview route: its period, currency and freshness. */
  private overviewMetaFor(meta: OverviewMetaOut, filters: GlobalFilters, now: Date): DataMeta {
    return overviewMeta(meta, filters, now, {
      lastUpdatedAt: meta.freshness.latest_source_updated_at,
      currency: meta.currency,
      coverage: null,
    })
  }

  /**
   * A read framed as the Overview's (`from`/`to` for the filters, its `meta`): the path is
   * built and the meta read against the same clock, and the body mapped by `map`.
   */
  private async overviewRead<Out extends { meta: OverviewMetaOut }, T>(
    filters: GlobalFilters,
    path: (now: Date) => string,
    map: (body: Out) => T,
    init?: RequestInit,
  ): Promise<Envelope<T>> {
    const now = new Date()
    const body = await this.send<Out>(path(now), init)
    return { meta: this.overviewMetaFor(body.meta, filters, now), data: map(body) }
  }

  async overviewKpis(filters: GlobalFilters): Promise<Envelope<Kpi[]>> {
    const now = new Date()
    const body = await this.send<KpisOut>(`/overview/kpis?${overviewSearch(filters, now)}`)
    return { meta: this.overviewMetaFor(body.meta, filters, now), data: kpisFromWire(body, filters) }
  }

  async revenueTrend(filters: GlobalFilters, window: TrendWindow): Promise<Envelope<RevenueTrend>> {
    const now = new Date()
    const body = await this.send<RevenueTrendOut>(`/overview/revenue-trend?${revenueTrendSearch(filters, window, now)}`)
    const meta = this.overviewMetaFor(body.meta, trendFilters(filters, window), now)
    return { meta, data: revenueTrendFromWire(body, window, meta) }
  }

  async funnel(filters: GlobalFilters): Promise<Envelope<FunnelStage[]>> {
    const now = new Date()
    const body = await this.send<FunnelOut>(`/overview/funnel?${overviewSearch(filters, now)}`)
    const { stages, coverage } = funnelFromWire(body, filters)
    // The funnel is as fresh as the event log; the API reports no time for it.
    return { meta: overviewMeta(body.meta, filters, now, { lastUpdatedAt: null, currency: null, coverage }), data: stages }
  }

  async topCompetitions(filters: GlobalFilters, limit: number): Promise<Envelope<CompetitionPerformance[]>> {
    const now = new Date()
    const body = await this.send<TopCompetitionsOut>(`/overview/top-competitions?${topCompetitionsSearch(filters, limit, now)}`)
    return { meta: this.overviewMetaFor(body.meta, filters, now), data: topCompetitionsFromWire(body) }
  }

  async channelPerformance(filters: GlobalFilters): Promise<Envelope<ChannelPerformance[]>> {
    const now = new Date()
    const body = await this.send<ChannelPerformanceOut>(`/overview/channel-performance?${channelPerformanceSearch(filters, now)}`)
    return {
      meta: overviewMeta(body.meta, filters, now, { lastUpdatedAt: null, currency: body.meta.currency, coverage: null }),
      data: channelPerformanceFromWire(body),
    }
  }

  // --- insights: alerts, trends and the digest (live, wave 17; api/insights.ts) ----

  /** `GET /insights/alerts`: the open alerts not dismissed, as the API persisted their state. */
  async activeAlerts(): Promise<Envelope<Alert[]>> {
    const body = await this.send<InsightAlertsOut>(activeAlertsPath())
    return { meta: { ...this.liveMeta(), truncated: body.meta.truncated }, data: body.items.map(insightAlertFromWire) }
  }

  async acknowledgeAlert(id: string): Promise<void> {
    await this.send<InsightAlertOut>(alertActionPath(id, 'acknowledge'), { method: 'POST' })
  }

  async dismissAlert(id: string): Promise<void> {
    await this.send<InsightAlertOut>(alertActionPath(id, 'dismiss'), { method: 'POST' })
  }

  async trends(): Promise<Envelope<Trend[]>> {
    const body = await this.send<TrendsOut>(TRENDS_PATH)
    return { meta: trendsMeta(body.meta), data: body.items.map(trendFromWire) }
  }

  async digest(query: DigestQuery): Promise<Envelope<DigestReport>> {
    return { meta: this.liveMeta(), data: digestReportFromWire(await this.send<DigestReportOut>(digestPath(query))) }
  }

  async regenerateDigest(period: DigestPeriod): Promise<Envelope<DigestReport>> {
    const body = await this.send<DigestReportOut>(regenerateDigestPath(period), { method: 'POST' })
    return { meta: this.liveMeta(), data: digestReportFromWire(body) }
  }

  // --- from here, the delegation list (NOT_LIVE_READS, NOT_LIVE_MUTATIONS): not live yet ---

  // --- insights: not live yet --------------------------------------------------

  recommendations(filters: GlobalFilters) {
    return this.notLive.recommendations(filters)
  }

  dismissRecommendation(id: string) {
    return this.notLive.dismissRecommendation(id)
  }

  automations() {
    return this.notLive.automations()
  }

  // --- automation: not live yet ---------------------------------------------

  triggerDefinitions(): Promise<TriggerDefinition[]> {
    return this.notLive.triggerDefinitions()
  }

  actionDefinitions(): Promise<ActionDefinition[]> {
    return this.notLive.actionDefinitions()
  }

  workflows() {
    return this.notLive.workflows()
  }

  workflow(id: string) {
    return this.notLive.workflow(id)
  }

  setWorkflowStatus(id: string, status: AutomationStatus) {
    return this.notLive.setWorkflowStatus(id, status)
  }

  scheduledActions() {
    return this.notLive.scheduledActions()
  }

  cancelScheduledAction(id: string) {
    return this.notLive.cancelScheduledAction(id)
  }

  automationActivity(): Promise<Envelope<ActivityEntry[]>> {
    return this.notLive.automationActivity()
  }

  // --- reports: not live yet ------------------------------------------------

  savedReports() {
    return this.notLive.savedReports()
  }

  savedReport(id: string) {
    return this.notLive.savedReport(id)
  }

  createSavedReport(input: ReportInput) {
    return this.notLive.createSavedReport(input)
  }

  duplicateSavedReport(id: string) {
    return this.notLive.duplicateSavedReport(id)
  }

  scheduleSavedReport(id: string, schedule: ReportSchedule | null) {
    return this.notLive.scheduleSavedReport(id, schedule)
  }

  reportRows(dataset: ReportDataset, filters: GlobalFilters): Promise<Envelope<ReportRow[]>> {
    return this.notLive.reportRows(dataset, filters)
  }

  exports(): Promise<Envelope<ReportExport[]>> {
    return this.notLive.exports()
  }

  requestExport(input: RequestExportInput) {
    return this.notLive.requestExport(input)
  }

  // --- market intelligence and listings: not live yet ----------------------

  marketCompetitors(filters: GlobalFilters) {
    return this.notLive.marketCompetitors(filters)
  }

  marketTrackedCompetitions(filters: GlobalFilters) {
    return this.notLive.marketTrackedCompetitions(filters)
  }

  marketObservations(filters: GlobalFilters) {
    return this.notLive.marketObservations(filters)
  }

  marketActivitySummary(filters: GlobalFilters) {
    return this.notLive.marketActivitySummary(filters)
  }

  marketPricingSummary(filters: GlobalFilters) {
    return this.notLive.marketPricingSummary(filters)
  }

  marketPrizeTrends(filters: GlobalFilters) {
    return this.notLive.marketPrizeTrends(filters)
  }

  marketMonitoringStatus() {
    return this.notLive.marketMonitoringStatus()
  }

  addMarketCompetitor(input: CompetitorInput) {
    return this.notLive.addMarketCompetitor(input)
  }

  importMarketCompetitorsCsv(csv: string) {
    return this.notLive.importMarketCompetitorsCsv(csv)
  }

  addTrackedCompetition(input: TrackedCompetitionInput) {
    return this.notLive.addTrackedCompetition(input)
  }

  importTrackedCompetitionsCsv(csv: string) {
    return this.notLive.importTrackedCompetitionsCsv(csv)
  }

  listings() {
    return this.notLive.listings()
  }

  listing(id: string) {
    return this.notLive.listing(id)
  }

  createListing(input: ListingInput) {
    return this.notLive.createListing(input)
  }

  updateListing(id: string, input: ListingInput) {
    return this.notLive.updateListing(id, input)
  }

  generateListingContent(id: string) {
    return this.notLive.generateListingContent(id)
  }

  setListingApproval(id: string, group: FieldGroupKey, approved: boolean) {
    return this.notLive.setListingApproval(id, group, approved)
  }

  publishListing(id: string, target: PublishTargetKey) {
    return this.notLive.publishListing(id, target)
  }

  listingTemplates() {
    return this.notLive.listingTemplates()
  }

  saveListingTemplate(input: ListingTemplateInput) {
    return this.notLive.saveListingTemplate(input)
  }

  // --- campaigns: /api/v1/campaigns ------------------------------------------

  private async send<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    })
    if (!response.ok) throw await apiErrorFrom(response)
    if (response.status === 204) return undefined as T
    return (await response.json()) as T
  }

  /**
   * A file export, fetched rather than linked: a refusal (a role without
   * `exports.create`, say) rejects with the API's error instead of being saved as
   * a `.csv` of JSON. Answers the checked response, so a caller can read its
   * headers as well as its body.
   */
  private async fetchExport(path: string): Promise<Response> {
    const response = await fetch(`${API_BASE}${path}`, { credentials: 'same-origin' })
    if (!response.ok) throw await apiErrorFrom(response)
    return response
  }

  private liveMeta(filters?: GlobalFilters): DataMeta {
    if (!filters) return { source: 'live', lastUpdatedAt: null }
    const period = resolvePeriod(filters, new Date())
    return {
      source: 'live',
      lastUpdatedAt: null,
      period: {
        from: period.current.from,
        to: period.current.to,
        label: period.label,
        compareLabel: null,
        previousFrom: null,
        previousTo: null,
      },
    }
  }

  /** The API reads production unless environments are named, the same rule as every other read. */
  private performancePath(campaignId: string, filters: GlobalFilters): string {
    const { current } = resolvePeriod(filters, new Date())
    return withEnvironments(
      `/campaigns/${encodeURIComponent(campaignId)}/performance?start=${current.from}&end=${current.to}`,
      filters.environments,
    )
  }

  private async detailFor(row: CampaignOut): Promise<CampaignDetail> {
    const base = `/campaigns/${encodeURIComponent(row.id)}`
    const [channels, tracking] = await Promise.all([
      this.send<ListOut<ChannelOut>>(`${base}/channels`),
      this.send<ListOut<TrackingOut>>(`${base}/tracking`),
    ])
    const creatives = await Promise.all(
      channels.items.map((channel) =>
        this.send<ListOut<CreativeOut>>(`${base}/channels/${encodeURIComponent(channel.id)}/creatives`),
      ),
    )
    const detail = {
      campaign: campaignFromWire(row),
      channels: channels.items.map(channelFromWire),
      creatives: creatives.flatMap((list) => list.items.map(creativeFromWire)),
      tracking: tracking.items.map(trackingFromWire),
    }
    return { ...detail, activity: activityFromRecords(detail) }
  }

  private async detailById(id: string): Promise<Envelope<CampaignDetail>> {
    const row = await this.send<CampaignOut>(`/campaigns/${encodeURIComponent(id)}`)
    return { meta: this.liveMeta(), data: await this.detailFor(row) }
  }

  async campaignWebsites(): Promise<CampaignWebsite[]> {
    return (await api.websites()).map(websiteFromWire)
  }

  async campaigns(filters: GlobalFilters): Promise<Envelope<CampaignListItem[]>> {
    const list = await this.send<ListOut<CampaignOut>>('/campaigns')
    const data = await Promise.all(
      list.items.map(async (row) => {
        // One campaign's channels or figures failing to load must not hide the whole list.
        const [channels, performance] = await Promise.allSettled([
          this.send<ListOut<ChannelOut>>(`/campaigns/${encodeURIComponent(row.id)}/channels`),
          this.send<CampaignPerformanceOut>(this.performancePath(row.id, filters)),
        ])
        return {
          campaign: campaignFromWire(row),
          channels:
            channels.status === 'fulfilled'
              ? channels.value.items.map(channelFromWire).map(({ id, name, channelType, kind }) => ({ id, name, channelType, kind }))
              : [],
          totals: performance.status === 'fulfilled' ? performanceFromWire(performance.value).totals : emptyMetrics(),
          // Each flag covers its own request, so one outage never hides what the other returned.
          ...(channels.status === 'fulfilled' ? {} : { channelsUnavailable: true }),
          ...(performance.status === 'fulfilled' ? {} : { performanceUnavailable: true }),
        }
      }),
    )
    return { meta: this.liveMeta(filters), data }
  }

  async campaign(id: string): Promise<Envelope<CampaignDetail | null>> {
    try {
      return await this.detailById(id)
    } catch (error) {
      // Another tenant's campaign is a 404 too, and confirms nothing.
      if (error instanceof ApiError && error.status === 404) return { meta: this.liveMeta(), data: null }
      throw error
    }
  }

  async campaignPerformance(id: string, filters: GlobalFilters): Promise<Envelope<CampaignPerformance>> {
    const row = await this.send<CampaignPerformanceOut>(this.performancePath(id, filters))
    return { meta: this.liveMeta(filters), data: performanceFromWire(row) }
  }

  /**
   * The campaign first, as a draft; then each channel with its creative and
   * tracking, in order; and only then, if launching, the status change. A
   * failure part-way leaves a draft to finish, never a half-built live campaign.
   */
  async createCampaign(draft: CampaignDraft, intent: SaveIntent): Promise<Envelope<CampaignDetail>> {
    if (!draft.objective || draft.objective === 'custom') {
      throw new ApiError(422, 'validation_error', 'Custom objectives are not supported by the campaigns API yet. Choose one of the listed objectives.')
    }
    const refs = audienceToRefs(draft.audience)
    const created = await this.send<CampaignOut>('/campaigns', {
      method: 'POST',
      body: JSON.stringify({
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        objective: OBJECTIVE_TO_API[draft.objective],
        website_id: draft.websiteId,
        status: 'draft',
        competition_ids: draft.competitionIds,
        starts_at: toWireDate(draft.startsAt, 'start'),
        ends_at: toWireDate(draft.endsAt, 'end'),
        total_budget: draft.totalBudget,
        audience_include_refs: refs.include,
        audience_exclude_refs: refs.exclude,
        automation_refs: draft.automations.map(automationRef),
      }),
    })
    const base = `/campaigns/${encodeURIComponent(created.id)}`

    // From here on the campaign exists: a failure must point at it, or a retry would create it twice.
    try {
      for (const channel of draft.channels) {
        const name = channel.name.trim()
        const row = await this.send<ChannelOut>(`${base}/channels`, {
          method: 'POST',
          body: JSON.stringify({
            name,
            channel_type: channel.channelType,
            ...(channel.channelType === 'custom' ? { kind: channel.kind } : {}),
            status: intent === 'launch' ? 'active' : 'draft',
            budget: isOwnedChannel(channel.channelType) ? null : channel.budget,
          }),
        })
        const { headline, body, assetUrl } = channel.creative
        if (headline.trim() || body.trim() || assetUrl.trim()) {
          const slot = CREATIVE_SLOTS[channel.channelType]
          await this.send<CreativeOut>(`${base}/channels/${encodeURIComponent(row.id)}/creatives`, {
            method: 'POST',
            body: JSON.stringify({
              name: `${name} — ${slot.label}`,
              creative_type: slot.creativeType,
              headline: headline.trim() || null,
              body: body.trim() || null,
              asset_url: assetUrl.trim() || null,
            }),
          })
        }
        const tracking = channel.tracking
        await this.send<TrackingOut>(`${base}/tracking`, {
          method: 'POST',
          body: JSON.stringify({
            channel_id: row.id,
            landing_path: tracking.landingPath || null,
            utm_source: tracking.utmSource || null,
            utm_medium: tracking.utmMedium || null,
            utm_content: tracking.utmContent || null,
            short_link: tracking.shortLink,
            qr_code: tracking.qrCode,
            ...(tracking.promoMode === 'existing' ? { promo_code: tracking.promoCode.trim() } : {}),
            generate_promo_code: tracking.promoMode === 'generate',
          }),
        })
      }

      if (intent === 'launch') {
        const status = activationStatus(null, draft.startsAt, toDateKey(new Date()))
        await this.send<CampaignOut>(base, { method: 'PATCH', body: JSON.stringify({ status }) })
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'an unexpected error'
      throw new CampaignPartiallySavedError(`The campaign was saved as a draft, but not everything could be added: ${reason}`, created.id)
    }
    return this.detailById(created.id)
  }

  async updateCampaign(id: string, patch: CampaignPatch): Promise<Envelope<CampaignDetail>> {
    const path = `/campaigns/${encodeURIComponent(id)}`
    const body: Record<string, unknown> = {}
    if (patch.name !== undefined) body.name = patch.name
    if (patch.description !== undefined) body.description = patch.description
    if (patch.status !== undefined) body.status = patch.status
    if (patch.totalBudget !== undefined) body.total_budget = patch.totalBudget
    if (patch.startsAt !== undefined) body.starts_at = toWireDate(patch.startsAt, 'start')
    if (patch.endsAt !== undefined) body.ends_at = toWireDate(patch.endsAt, 'end')
    if (patch.competitionIds !== undefined) body.competition_ids = patch.competitionIds
    if (patch.audience !== undefined) {
      const refs = audienceToRefs(patch.audience)
      body.audience_include_refs = refs.include
      body.audience_exclude_refs = refs.exclude
    }
    if (patch.automations !== undefined) {
      // Keep references this app did not write: they belong to someone else's automation.
      const current = campaignFromWire(await this.send<CampaignOut>(path))
      body.automation_refs = [...patch.automations.map(automationRef), ...current.otherAutomationRefs]
    }
    await this.send<CampaignOut>(path, { method: 'PATCH', body: JSON.stringify(body) })
    return this.detailById(id)
  }

  async updateCampaignChannel(campaignId: string, channelId: string, patch: ChannelPatch): Promise<Envelope<CampaignDetail>> {
    await this.send<ChannelOut>(`/campaigns/${encodeURIComponent(campaignId)}/channels/${encodeURIComponent(channelId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
    return this.detailById(campaignId)
  }

  async updateCampaignCreative(
    campaignId: string,
    creative: Pick<CampaignCreative, 'id' | 'channelId'>,
    patch: CreativePatch,
  ): Promise<Envelope<CampaignDetail>> {
    const body: Record<string, unknown> = {}
    if (patch.headline !== undefined) body.headline = patch.headline
    if (patch.body !== undefined) body.body = patch.body
    if (patch.assetUrl !== undefined) body.asset_url = patch.assetUrl
    await this.send<CreativeOut>(
      `/campaigns/${encodeURIComponent(campaignId)}/channels/${encodeURIComponent(creative.channelId)}/creatives/${encodeURIComponent(creative.id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    )
    return this.detailById(campaignId)
  }

  async generateCampaignTracking(campaignId: string, input: TrackingGenerateInput): Promise<Envelope<CampaignDetail>> {
    await this.send<TrackingOut>(`/campaigns/${encodeURIComponent(campaignId)}/tracking`, {
      method: 'POST',
      body: JSON.stringify({
        channel_id: input.channelId,
        short_link: input.shortLink,
        qr_code: input.qrCode,
        generate_promo_code: input.generatePromoCode,
      }),
    })
    return this.detailById(campaignId)
  }

  async updateCampaignTracking(campaignId: string, trackingId: string, patch: TrackingPatch): Promise<Envelope<CampaignDetail>> {
    const body: Record<string, unknown> = {}
    if (patch.status !== undefined) body.status = patch.status
    if (patch.landingPath !== undefined) body.landing_path = patch.landingPath
    await this.send<TrackingOut>(`/campaigns/${encodeURIComponent(campaignId)}/tracking/${encodeURIComponent(trackingId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
    return this.detailById(campaignId)
  }

  async campaignQrSvg(campaignId: string, trackingId: string): Promise<string> {
    const response = await fetch(
      `/api/v1/campaigns/${encodeURIComponent(campaignId)}/tracking/${encodeURIComponent(trackingId)}/qr?format=svg`,
      { credentials: 'same-origin' },
    )
    if (!response.ok) throw new ApiError(response.status, response.status === 404 ? 'not_found' : 'temporary_failure', response.statusText)
    return response.text()
  }

  // --- integrations: Meta (wave 7b) --------------------------------------------

  async metaIntegration(): Promise<MetaIntegrationState> {
    return metaStateFromWire(await this.send<MetaIntegrationOut>('/integrations/meta'))
  }

  async connectMeta(): Promise<MetaConnectStart> {
    const body = await this.send<{ authorize_url: string }>('/integrations/meta/connect', { method: 'POST' })
    return { authorizeUrl: body.authorize_url, completesInApp: false }
  }

  async disconnectMeta(): Promise<MetaIntegrationState> {
    return metaStateFromWire(await this.send<MetaIntegrationOut>('/integrations/meta', { method: 'DELETE' }))
  }

  async runMetaImportNow(): Promise<MetaIntegrationState> {
    return metaStateFromWire(
      await this.send<MetaIntegrationOut>('/integrations/meta/import', { method: 'POST' }),
    )
  }

  async metaAdAccounts(): Promise<MetaAdAccountList> {
    return adAccountsFromWire(await this.send<AdAccountListOut>('/integrations/meta/ad-accounts'))
  }

  async saveMetaAdAccounts(mappings: readonly MetaAdAccountMapping[]): Promise<MetaMappingResult> {
    const body = await this.send<AdAccountMappingOut>('/integrations/meta/ad-accounts', {
      method: 'PUT',
      body: JSON.stringify(mappingToWire(mappings)),
    })
    return mappingFromWire(body)
  }

  // --- Google Analytics 4 (wave 7b) ---------------------------------------------

  async ga4Integration(): Promise<Ga4Integration> {
    return ga4IntegrationFromWire(await this.send<Ga4IntegrationOut>('/integrations/ga4'))
  }

  /**
   * Connecting is the one GA4 call outside `send`: a refusal's `details.stream_hosts` /
   * `details.website_hosts` (when the server sent them) matter to this screen, and
   * `send`'s shared `apiErrorFrom` drops `details` entirely.
   */
  async connectGa4(input: { propertyId: string; websiteId?: string }): Promise<Ga4Property> {
    const response = await fetch(`${API_BASE}/integrations/ga4`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ property_id: input.propertyId, ...(input.websiteId ? { website_id: input.websiteId } : {}) }),
    })
    if (!response.ok) throw await ga4ConnectErrorFrom(response)
    return ga4PropertyFromWire((await response.json()) as Ga4PropertyOut)
  }

  async disconnectGa4(ga4PropertyId: string): Promise<Ga4Property> {
    const body = await this.send<Ga4PropertyOut>(`/integrations/ga4/${encodeURIComponent(ga4PropertyId)}`, { method: 'DELETE' })
    return ga4PropertyFromWire(body)
  }

  async runGa4Import(ga4PropertyId: string): Promise<Ga4Property[]>
  async runGa4Import(): Promise<Ga4ImportRun>
  async runGa4Import(ga4PropertyId?: string): Promise<Ga4Property[] | Ga4ImportRun> {
    if (ga4PropertyId) {
      const body = await this.send<Ga4PropertyOut>(`/integrations/ga4/${encodeURIComponent(ga4PropertyId)}/import`, { method: 'POST' })
      return [ga4PropertyFromWire(body)]
    }
    const body = await this.send<Ga4ImportRunOut>('/integrations/ga4/import', { method: 'POST' })
    return ga4ImportRunFromWire(body)
  }

  async checkGa4Streams(propertyId: string): Promise<Ga4StreamCheck> {
    const body = await this.send<Ga4StreamCheckOut>(`/integrations/ga4/properties/${encodeURIComponent(propertyId)}/streams`)
    return ga4StreamCheckFromWire(body)
  }

  async traffic(query: Ga4TrafficQuery): Promise<Ga4TrafficReport> {
    return trafficFromWire(await this.send<TrafficOut>(`/analytics/traffic?${trafficSearch(query)}`))
  }

  async trafficCsv(query: Ga4TrafficQuery): Promise<Blob> {
    return (await this.fetchExport(`/analytics/traffic.csv?${trafficSearch(query)}`)).blob()
  }

  // --- First-party traffic: the Traffic page's TmT source (wave 8, card 3d9c00e6) ---

  async firstPartyTraffic(query: FirstPartyTrafficQuery): Promise<FirstPartyTrafficReport> {
    return firstPartyTrafficFromWire(
      await this.send<FirstPartyTrafficOut>(`/analytics/traffic/first-party?${firstPartyTrafficSearch(query)}`),
    )
  }

  async firstPartyTrafficCsv(query: FirstPartyTrafficQuery): Promise<Blob> {
    return (await this.fetchExport(`/analytics/traffic/first-party/export?${firstPartyTrafficSearch(query)}`)).blob()
  }

  // --- Data quality: the tracking baseline (wave 20) ------------------------------

  async trackingBaseline(): Promise<TrackingBaseline> {
    return trackingBaselineFromWire(await this.send<TrackingBaselineOut>(DATA_QUALITY_BASELINE_PATH))
  }

  async setTrackingBaseline(input: TrackingBaselineInput): Promise<TrackingBaseline> {
    return trackingBaselineFromWire(
      await this.send<TrackingBaselineOut>(DATA_QUALITY_BASELINE_PATH, {
        method: 'PUT',
        body: JSON.stringify(trackingBaselineToWire(input)),
      }),
    )
  }

  // --- First-party attribution: Marketing › Attribution (card 3d9c00e6) ---------

  /** Production only, every website: the route takes no environment or website. */
  async attributionConversions(query: AttributionQuery): Promise<AttributionReport> {
    return attributionFromWire(
      await this.send<AttributionConversionsOut>(`/analytics/attribution/conversions?${attributionSearch(query)}`),
    )
  }

  // --- Meta advertising campaign report (wave 7b) -------------------------------

  async advertisingCampaigns(query: AdvertisingQuery): Promise<AdvertisingReport> {
    return advertisingReportFromWire(
      await this.send<AdvertisingCampaignReportOut>(`/analytics/advertising/campaigns?${advertisingSearch(query)}`),
    )
  }

  async advertisingCampaignsCsv(query: AdvertisingQuery): Promise<Blob> {
    return (await this.fetchExport(`/analytics/advertising/campaigns/export?${advertisingSearch(query)}`)).blob()
  }

  // --- Marketing › Advertising: the overview and the reconciliation (wave 16) ------

  async advertisingOverview(query: AdOverviewQuery): Promise<AdvertisingOverview> {
    return advertisingOverviewFromWire(
      await this.send<AdvertisingOverviewOut>(`/analytics/advertising/overview?${advertisingOverviewSearch(query, new Date())}`),
    )
  }

  async advertisingOverviewTrend(query: AdOverviewTrendQuery): Promise<AdvertisingOverviewTrend> {
    return advertisingOverviewTrendFromWire(
      await this.send<AdvertisingOverviewTrendOut>(
        `/analytics/advertising/overview/trend?${advertisingOverviewTrendSearch(query, new Date())}`,
      ),
    )
  }

  async adAttributionReconciliation(query: AdReconciliationQuery): Promise<AdAttributionReconciliation> {
    return adAttributionReconciliationFromWire(
      await this.send<AdAttributionReconciliationOut>(`/analytics/advertising/reconciliation?${adReconciliationSearch(query)}`),
    )
  }

  async adAttributionReconciliationCsv(query: AdReconciliationQuery): Promise<Blob> {
    return (await this.fetchExport(`/analytics/advertising/reconciliation/export?${adReconciliationSearch(query)}`)).blob()
  }
  async recoverableCheckoutsSummary(query: RecoveryQuery): Promise<RecoverableCheckoutsSummary> {
    return recoverySummaryFromWire(
      await this.send<RecoverableCheckoutsSummaryOut>(recoveryPath('/recovery/checkouts/summary', query)),
    )
  }

  async recoverableCheckouts(query: RecoveryQuery): Promise<RecoverableCheckoutsList> {
    return recoveryListFromWire(await this.send<RecoverableCheckoutsListOut>(recoveryPath('/recovery/checkouts', query)))
  }

  async recoverableCheckoutsCsv(query: RecoveryQuery): Promise<Blob> {
    return (await this.fetchExport(recoveryPath('/recovery/checkouts/export', query))).blob()
  }

  // --- the competitions list (wave 12, api/competitions.ts) ----------------------

  async competitions(query: CompetitionsQuery): Promise<Envelope<CompetitionsList>> {
    return this.overviewRead(query.filters, (now) => `/analytics/competitions?${competitionsSearch(query, now)}`, competitionsFromWire)
  }

  /** The file's headers say its name and whether it was cut (`csvFileFromResponse`). */
  async competitionsCsv(query: CompetitionsQuery): Promise<CsvFile> {
    const search = competitionsSearch({ ...query, limit: undefined, offset: undefined }, new Date())
    return csvFileFromResponse(await this.fetchExport(`/analytics/competitions.csv?${search}`))
  }

  // --- one competition's page (wave 15, api/competitionDetail.ts) -------------------

  async competitionDetail(query: CompetitionDetailQuery & { granularity?: SalesGranularity }): Promise<Envelope<CompetitionDetail>> {
    const { competitionId, filters, granularity } = query
    return this.overviewRead<CompetitionDetailOut, CompetitionDetail>(
      filters,
      (now) => `${competitionDetailPath(competitionId)}?${competitionDetailSearch(filters, now, { granularity })}`,
      competitionDetailFromWire,
    )
  }

  async competitionFunnel({ competitionId, filters }: CompetitionDetailQuery): Promise<Envelope<CompetitionFunnel>> {
    return this.overviewRead<CompetitionFunnelOut, CompetitionFunnel>(
      filters,
      (now) => `${competitionDetailPath(competitionId, 'funnel')}?${competitionDetailSearch(filters, now)}`,
      competitionFunnelFromWire,
    )
  }

  async competitionSources(query: CompetitionDetailQuery & { model?: AttributionModel }): Promise<Envelope<CompetitionSources>> {
    const { competitionId, filters, model } = query
    return this.overviewRead<CompetitionSourcesOut, CompetitionSources>(
      filters,
      (now) => `${competitionDetailPath(competitionId, 'sources')}?${competitionDetailSearch(filters, now, { model })}`,
      competitionSourcesFromWire,
    )
  }

  async competitionDevices({ competitionId, filters }: CompetitionDetailQuery): Promise<Envelope<CompetitionDevices>> {
    return this.overviewRead<CompetitionDevicesOut, CompetitionDevices>(
      filters,
      (now) => `${competitionDetailPath(competitionId, 'devices')}?${competitionDetailSearch(filters, now)}`,
      competitionDevicesFromWire,
    )
  }

  async competitionAdvertising({ competitionId, filters }: CompetitionDetailQuery): Promise<Envelope<CompetitionAdvertising>> {
    return this.overviewRead<CompetitionAdvertisingOut, CompetitionAdvertising>(
      filters,
      (now) => `${competitionDetailPath(competitionId, 'advertising')}?${competitionDetailSearch(filters, now)}`,
      competitionAdvertisingFromWire,
    )
  }

  // --- the Revenue page (wave 18, api/revenue.ts) -----------------------------------

  /**
   * A revenue route's answer, framed as the Overview's: the period the API computed and the
   * comparison it read (weekday-aligned, so named by its own dates), its currency, and the
   * newest source row behind the figures.
   */
  private async revenueRead<Out extends { meta: RevenueMetaOut }, T>(
    filters: GlobalFilters,
    path: (now: Date) => string,
    map: (body: Out) => T,
  ): Promise<Envelope<T>> {
    const now = new Date()
    const body = await this.send<Out>(path(now))
    const meta = overviewMeta(body.meta, filters, now, {
      lastUpdatedAt: body.meta.freshness.latest_source_updated_at,
      currency: body.meta.currency,
      coverage: null,
    })
    return { meta, data: map(body) }
  }

  revenueSummary(query: RevenueQuery): Promise<Envelope<RevenueSummary>> {
    return this.revenueRead<RevenueSummaryOut, RevenueSummary>(
      query.filters,
      (now) => `${REVENUE_PATHS.summary}?${revenueSearch(query, now).toString()}`,
      revenueSummaryFromWire,
    )
  }

  revenueSeries(query: RevenueSeriesQuery): Promise<Envelope<RevenueSeries>> {
    return this.revenueRead<RevenueSeriesOut, RevenueSeries>(
      query.filters,
      (now) => `${REVENUE_PATHS.trend}?${revenueSeriesSearch(query, now)}`,
      revenueSeriesFromWire,
    )
  }

  revenueBreakdown(query: RevenueBreakdownQuery): Promise<Envelope<RevenueBreakdown>> {
    return this.revenueRead<RevenueBreakdownOut, RevenueBreakdown>(
      query.filters,
      (now) => `${REVENUE_PATHS.breakdown}?${revenueBreakdownSearch(query, now)}`,
      revenueBreakdownFromWire,
    )
  }

  revenueTiming(query: RevenueQuery): Promise<Envelope<RevenueTiming>> {
    return this.revenueRead<RevenueTimingOut, RevenueTiming>(
      query.filters,
      (now) => `${REVENUE_PATHS.timing}?${revenueSearch(query, now).toString()}`,
      revenueTimingFromWire,
    )
  }

  // --- the Revenue page's lifecycle, leakage and orders (wave 18 stage B, api/revenueLeakage.ts)

  revenueLifecycle(query: LifecycleQuery): Promise<Envelope<RevenueLifecycle>> {
    return this.revenueRead<RevenueLifecycleOut, RevenueLifecycle>(
      query.filters,
      (now) => `${REVENUE_LEAKAGE_PATHS.lifecycle}?${lifecycleSearch(query, now)}`,
      lifecycleFromWire,
    )
  }

  revenueLeakage(query: LeakageQuery): Promise<Envelope<RevenueLeakage>> {
    return this.revenueRead<RevenueLeakageOut, RevenueLeakage>(
      query.filters,
      (now) => `${REVENUE_LEAKAGE_PATHS.leakage}?${leakageSearch(query, now)}`,
      leakageFromWire,
    )
  }

  revenueOrders(query: OrdersQuery): Promise<Envelope<RevenueOrders>> {
    return this.revenueRead<RevenueOrdersOut, RevenueOrders>(
      query.filters,
      (now) => `${REVENUE_LEAKAGE_PATHS.orders}?${ordersSearch(query, now)}`,
      ordersFromWire,
    )
  }

  /** The file's headers say its name and whether it was cut (`csvFileFromResponse`). */
  async revenueOrdersCsv(query: OrdersQuery): Promise<CsvFile> {
    return csvFileFromResponse(await this.fetchExport(`${REVENUE_LEAKAGE_PATHS.orders}?${ordersSearch(query, new Date(), true)}`))
  }

  // --- the customers list and sign-ups (wave 13, api/customers.ts, api/signups.ts) --

  async customers(query: CustomersQuery): Promise<Envelope<CustomersList>> {
    return this.overviewRead(query.filters, (now) => `/analytics/customers?${customersSearch(query, now)}`, customersFromWire)
  }

  /** A `POST` so the term travels in the body, never in a URL (the API's access log and the browser's history keep URLs). */
  async searchCustomers(query: CustomersQuery, search: CustomerSearch): Promise<Envelope<CustomersList>> {
    return this.overviewRead(query.filters, (now) => `/analytics/customers/search?${customersSearch(query, now)}`, customersFromWire, {
      method: 'POST',
      body: JSON.stringify(customerSearchBody(search)),
    })
  }

  async customersCsv(query: CustomersQuery): Promise<CsvFile> {
    const search = customersSearch({ ...query, limit: undefined, offset: undefined }, new Date())
    return csvFileFromResponse(await this.fetchExport(`/analytics/customers.csv?${search}`))
  }

  async revealCustomer(customerId: string): Promise<CustomerReveal> {
    const body = await this.send<CustomerRevealOut>(`/analytics/customers/${encodeURIComponent(customerId)}/reveal`, {
      method: 'POST',
    })
    return customerRevealFromWire(body)
  }

  async signups(filters: GlobalFilters): Promise<Envelope<SignupsReport>> {
    return this.overviewRead(filters, (now) => `/analytics/signups?${overviewSearch(filters, now)}`, signupsFromWire)
  }

  // --- connector health (wave 9) -------------------------------------------------

  async connectorWebsites(): Promise<CampaignWebsite[]> {
    return (await api.websites(ALL_ENVIRONMENTS)).map(websiteFromWire)
  }

  async websiteHealth(websiteId: string): Promise<WebsiteHealth> {
    return websiteHealthFromWire(
      await this.send<WebsiteHealthOut>(`/integrations/websites/${encodeURIComponent(websiteId)}/health`),
    )
  }

}

// --- connector health: the API's wire shapes and their mapping (wave 9) -------------

/** A tenant website as every picker and card names it. */
function websiteFromWire(website: Website): CampaignWebsite {
  return { id: website.id, name: website.name, origin: website.primary_origin, environment: website.environment }
}

/** Keyed by `Environment`, so an environment added to the type fails to compile until it is listed here. */
const EVERY_ENVIRONMENT: Record<Environment, true> = { production: true, staging: true, test: true, development: true }
const ALL_ENVIRONMENTS = Object.keys(EVERY_ENVIRONMENT) as Environment[]

/** `ConnectorHealthReportOut`. */
export interface ConnectorHealthReportOut {
  reported_at: string
  connector_version: string
  contract_version: string
  last_reconcile_started_at: string | null
  last_reconcile_finished_at: string | null
  last_reconcile_window_from: string | null
  last_reconcile_window_to: string | null
  last_reconcile_sent: number
  last_reconcile_skipped: number
  last_reconcile_failed: number
  last_reconcile_error_class: string | null
  next_run_due_at: string | null
  missed_run: boolean
  queue_backlog: number
  queue_parked: number
  queue_oldest_age_seconds: number | null
}

/** `WebsiteHealthOut`. */
export interface WebsiteHealthOut {
  website_id: string
  environment: Environment
  status: WebsiteHealthStatus
  last_event_at: { page_views: string | null; commerce: string | null; entities: string | null }
  quarantined_last_24h: number
  report: ConnectorHealthReportOut | null
  computed_at: string
}

export function websiteHealthFromWire(wire: WebsiteHealthOut): WebsiteHealth {
  const report = wire.report
  return {
    websiteId: wire.website_id,
    environment: wire.environment,
    status: wire.status,
    lastEventAt: {
      pageViews: wire.last_event_at.page_views,
      commerce: wire.last_event_at.commerce,
      entities: wire.last_event_at.entities,
    },
    quarantinedLast24h: wire.quarantined_last_24h,
    report: report
      ? {
          reportedAt: report.reported_at,
          connectorVersion: report.connector_version,
          contractVersion: report.contract_version,
          lastReconcileStartedAt: report.last_reconcile_started_at,
          lastReconcileFinishedAt: report.last_reconcile_finished_at,
          lastReconcileWindowFrom: report.last_reconcile_window_from,
          lastReconcileWindowTo: report.last_reconcile_window_to,
          lastReconcileSent: report.last_reconcile_sent,
          lastReconcileSkipped: report.last_reconcile_skipped,
          lastReconcileFailed: report.last_reconcile_failed,
          lastReconcileErrorClass: report.last_reconcile_error_class,
          nextRunDueAt: report.next_run_due_at,
          missedRun: report.missed_run,
          queueBacklog: report.queue_backlog,
          queueParked: report.queue_parked,
          queueOldestAgeSeconds: report.queue_oldest_age_seconds,
        }
      : null,
    computedAt: wire.computed_at,
  }
}
